import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { BACKEND_URL } from '../config/canonical';
import {
  FileText, User, Briefcase, Users, Car, Upload, Shield,
  Clock, CheckCircle2, XCircle, AlertTriangle, ChevronRight,
  Eye, ArrowRight, MessageSquare, RefreshCw, Search, Filter,
  Download, ExternalLink, FileSignature, Phone, Mail, MapPin,
  Calendar, DollarSign, Scale, Loader2
} from 'lucide-react';

const STATUS_COLORS = {
  SUBMITTED: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  UNDER_REVIEW: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  MORE_INFO_REQUESTED: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  APPROVED: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  DECLINED: 'bg-red-500/20 text-red-300 border-red-500/30',
  CONVERTED: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
};

const STATUS_ICONS = {
  SUBMITTED: Clock,
  UNDER_REVIEW: Eye,
  MORE_INFO_REQUESTED: MessageSquare,
  APPROVED: CheckCircle2,
  DECLINED: XCircle,
  CONVERTED: ArrowRight,
};

const CASE_TYPE_LABELS = {
  DOMESTIC_INVESTIGATION: 'Domestic Investigation',
  INFIDELITY: 'Infidelity Investigation',
  CHILD_CUSTODY: 'Child Custody Investigation',
  SURVEILLANCE: 'Surveillance Request',
  BACKGROUND_CHECK: 'Background Investigation',
  ASSET_SEARCH: 'Asset Search',
  MISSING_PERSON: 'Missing Person Investigation',
  INSURANCE_FRAUD: 'Fraud Investigation',
  LEGAL_SUPPORT: 'Legal Support Investigation',
  OTHER: 'Other',
};

// --- Helpers ------------------------------------------------------------
const showOrNA = (v) => {
  if (v === null || v === undefined) return 'N/A';
  const s = String(v).trim();
  return s ? s : 'N/A';
};

const SectionShell = ({ id, icon: Icon, title, count, children, accent = 'blue' }) => (
  <section id={id} className="scroll-mt-4" data-testid={`intake-section-${id}`}>
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-slate-700 bg-slate-800/80">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 text-${accent}-400`} />
          <h3 className="text-sm font-semibold text-white tracking-wide">{title}</h3>
        </div>
        {count != null && (
          <Badge variant="outline" className="border-slate-600 text-slate-300 text-[10px] font-mono">
            {count}
          </Badge>
        )}
      </header>
      <div className="p-4 text-sm">{children}</div>
    </div>
  </section>
);

const KV = ({ label, value, full = false }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <p className="text-slate-500 text-xs uppercase tracking-wider">{label}</p>
    <p className="text-white whitespace-pre-wrap break-words">{showOrNA(value)}</p>
  </div>
);

// Full Intake Record — comprehensive admin view with 9 sections + side nav.
const FullIntakeRecord = ({
  intake, actionLoading, addingNote, newInternalNote,
  setNewInternalNote, onAddNote, onUpdateStatus, onRequestInfo, onConvertToCase,
}) => {
  const c = intake.client || {};
  const cd = intake.case_details || {};
  const subjects = intake.subjects || [];
  const witnesses = intake.witnesses || cd.witnesses || [];
  const vehicles = intake.vehicles || [];
  const files = intake.files || [];
  const notes = intake.internal_notes || [];
  const history = intake.status_history || [];

  const attorneyInvolved = !!(cd.attorney_involved || cd.attorney_name || cd.law_firm);

  const NAV = [
    { id: 'sec-client',     label: 'Client',     icon: User },
    { id: 'sec-subject',    label: 'Subject',    icon: Users,         count: subjects.length },
    { id: 'sec-incident',   label: 'Incident',   icon: AlertTriangle },
    { id: 'sec-witness',    label: 'Witness',    icon: Users,         count: witnesses.length },
    { id: 'sec-attorney',   label: 'Attorney',   icon: Scale },
    { id: 'sec-vehicle',    label: 'Vehicle',    icon: Car,           count: vehicles.length },
    { id: 'sec-evidence',   label: 'Evidence',   icon: Upload,        count: files.length },
    { id: 'sec-timeline',   label: 'Timeline',   icon: Clock,         count: history.length },
    { id: 'sec-notes',      label: 'Notes',      icon: MessageSquare, count: notes.length },
    { id: 'sec-signature',  label: 'Signature',  icon: FileSignature },
  ];

  const jump = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    // Find the dialog scroll container and offset by the sticky header (~88px)
    const scrollContainer = el.closest('[role="dialog"]') || el.closest('.overflow-y-auto');
    if (scrollContainer && typeof scrollContainer.scrollTo === 'function') {
      const top = el.offsetTop - 96;
      scrollContainer.scrollTo({ top, behavior: 'smooth' });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="flex flex-col" data-testid="full-intake-record">
      <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur border-b border-slate-700 px-6 py-4">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between flex-wrap gap-2">
            <span className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              <span className="text-white">Full Intake Record</span>
              <span className="text-slate-400 font-mono text-sm">· {intake.id}</span>
            </span>
            <Badge className={STATUS_COLORS[intake.status] || ''}>
              {intake.status?.replace('_', ' ')}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap gap-2 mt-3">
          {intake.status !== 'CONVERTED' && intake.status !== 'DECLINED' && (
            <>
              <Button size="sm" onClick={onUpdateStatus} className="bg-blue-600 hover:bg-blue-700" data-testid="intake-update-status-btn">
                Update Status
              </Button>
              <Button size="sm" variant="outline" onClick={onRequestInfo}
                      className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10" data-testid="intake-request-info-btn">
                <MessageSquare className="w-4 h-4 mr-1" />
                Request Info
              </Button>
              {['APPROVED', 'SUBMITTED', 'UNDER_REVIEW'].includes(intake.status) && (
                <Button size="sm" onClick={onConvertToCase} disabled={actionLoading}
                        className="bg-emerald-600 hover:bg-emerald-700" data-testid="convert-to-case-btn">
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-1" />}
                  Convert to Case
                </Button>
              )}
            </>
          )}
          {intake.converted_case_id && (
            <Link to={`/cases/${intake.converted_case_id}`}>
              <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" data-testid="intake-view-case-btn">
                <ExternalLink className="w-4 h-4 mr-1" />
                View Case
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="flex">
        <aside className="hidden lg:block w-44 flex-shrink-0 border-r border-slate-800 sticky top-[88px] self-start max-h-[calc(92vh-88px)] overflow-y-auto py-4 px-2"
               data-testid="intake-side-nav">
          <ul className="space-y-0.5">
            {NAV.map(n => (
              <li key={n.id}>
                <button onClick={() => jump(n.id)}
                        className="w-full flex items-center justify-between gap-2 text-left text-xs text-slate-300 hover:text-amber-300 hover:bg-slate-800/60 rounded px-2 py-1.5 transition-colors"
                        data-testid={`intake-nav-${n.id}`}>
                  <span className="flex items-center gap-1.5">
                    <n.icon className="w-3.5 h-3.5" />
                    {n.label}
                  </span>
                  {n.count != null && n.count > 0 && (
                    <span className="font-mono text-[10px] text-amber-300">{n.count}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <div className="flex-1 px-4 sm:px-6 py-5 space-y-5 min-w-0">
          <SectionShell id="sec-client" icon={User} title="Client" accent="blue">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <KV label="Full Name" value={c.full_name} />
              <KV label="Date of Birth" value={c.date_of_birth} />
              <KV label="Email" value={c.email} />
              <KV label="Primary Phone" value={c.primary_phone} />
              <KV label="Secondary Phone" value={c.secondary_phone} />
              <KV label="Preferred Contact" value={c.preferred_contact} />
              <KV label="Driver's License" value={c.drivers_license ? `${c.drivers_license}${c.license_state ? ' (' + c.license_state + ')' : ''}` : null} />
              <KV label="Employer" value={c.employer} />
              <KV label="Address" full
                  value={[c.home_address, c.city, c.state, c.zip_code].filter(Boolean).join(', ') || null} />
            </div>
          </SectionShell>

          <SectionShell id="sec-subject" icon={Users} title="Subject(s)" count={subjects.length} accent="amber">
            {subjects.length === 0 ? (
              <p className="text-slate-400 italic">No subjects recorded in this intake.</p>
            ) : (
              <div className="space-y-3">
                {subjects.map((s, idx) => (
                  <div key={s.id || idx} className="p-3 bg-slate-700/40 rounded border border-slate-700">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <p className="text-white font-semibold">{showOrNA(s.full_name)}</p>
                      {idx === 0 && <Badge className="bg-blue-500/20 text-blue-300 text-xs">Primary</Badge>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      <KV label="Relationship" value={s.relationship_to_client} />
                      <KV label="DOB" value={s.date_of_birth} />
                      <KV label="Gender" value={s.gender} />
                      <KV label="Employer" value={s.employer} />
                      <KV label="Phone" value={s.phone} />
                      <KV label="Email" value={s.email} />
                      <KV label="Last Known Address" full
                          value={[s.last_known_address, s.city, s.state, s.zip_code].filter(Boolean).join(', ') || null} />
                      <KV label="Physical Description" full value={s.physical_description} />
                      <KV label="Notes" full value={s.notes} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionShell>

          <SectionShell id="sec-incident" icon={AlertTriangle} title="Incident / Case Details" accent="amber">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <KV label="Case Type" value={CASE_TYPE_LABELS[cd.case_type] || cd.custom_case_type || cd.case_type} />
              <KV label="Priority" value={cd.priority || 'STANDARD'} />
              <KV label="Investigation Location" value={cd.investigation_location} />
              <KV label="Deadlines" value={cd.deadlines} />
              <KV label="Incident Date" value={cd.incident_date} />
              <KV label="Incident Location" value={cd.incident_location} />
              <KV label="Description" full value={cd.description} />
              <KV label="Specific Goals" full value={cd.specific_goals} />
            </div>
          </SectionShell>

          <SectionShell id="sec-witness" icon={Users} title="Witness(es)" count={witnesses.length} accent="amber">
            {witnesses.length === 0 ? (
              <p className="text-slate-400 italic">No witnesses recorded in this intake.</p>
            ) : (
              <div className="space-y-3">
                {witnesses.map((w, idx) => (
                  <div key={w.id || idx} className="p-3 bg-slate-700/40 rounded border border-slate-700">
                    <p className="text-white font-semibold mb-2">{showOrNA(w.full_name || w.name)}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      <KV label="Relationship" value={w.relationship} />
                      <KV label="Phone" value={w.phone} />
                      <KV label="Email" value={w.email} />
                      <KV label="Statement" full value={w.statement || w.notes} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionShell>

          <SectionShell id="sec-attorney" icon={Scale} title="Attorney" accent="blue">
            {!attorneyInvolved ? (
              <p className="text-slate-400 italic">No attorney involved per intake submission.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <KV label="Attorney Name" value={cd.attorney_name} />
                <KV label="Law Firm" value={cd.law_firm} />
                <KV label="Phone" value={cd.attorney_phone} />
                <KV label="Email" value={cd.attorney_email} />
              </div>
            )}
          </SectionShell>

          <SectionShell id="sec-vehicle" icon={Car} title="Vehicle(s)" count={vehicles.length} accent="blue">
            {vehicles.length === 0 ? (
              <p className="text-slate-400 italic">No vehicles recorded in this intake.</p>
            ) : (
              <div className="space-y-3">
                {vehicles.map((v, idx) => (
                  <div key={v.id || idx} className="p-3 bg-slate-700/40 rounded border border-slate-700">
                    <p className="text-white font-semibold mb-2">
                      {[v.year, v.make, v.model].filter(Boolean).join(' ') || 'Unspecified Vehicle'}
                      {v.color ? ` · ${v.color}` : ''}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      <KV label="License Plate" value={v.license_plate} />
                      <KV label="Plate State" value={v.plate_state} />
                      <KV label="VIN" value={v.vin} />
                      <KV label="Registered Owner" value={v.registered_owner} />
                      <KV label="Notes" full value={v.notes} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionShell>

          <SectionShell id="sec-evidence" icon={Upload} title="Evidence / Uploaded Files" count={files.length} accent="emerald">
            {files.length === 0 ? (
              <p className="text-slate-400 italic">No files uploaded with this intake.</p>
            ) : (
              <div className="space-y-2">
                {files.map((f, idx) => {
                  const ct = (f.content_type || '').toLowerCase();
                  const fname = (f.file_name || '').toLowerCase();
                  const isImage = ct.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/.test(fname);
                  const mb = (Number(f.file_size || 0) / 1024 / 1024).toFixed(2);
                  return (
                    <div key={f.id || idx} className="p-2.5 bg-slate-700/40 rounded border border-slate-700 flex items-center gap-3"
                         data-testid={`intake-file-${idx}`}>
                      {isImage && f.file_url && (
                        <img src={f.file_url} alt={f.file_name}
                             className="w-14 h-14 object-cover rounded border border-slate-600 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white truncate text-sm">{showOrNA(f.file_name)}</p>
                        <p className="text-slate-500 text-xs">
                          {showOrNA(f.content_type)} · {mb} MB
                        </p>
                      </div>
                      {f.file_url && (
                        <a href={f.file_url} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="ghost" className="text-blue-400 h-7">
                            <Download className="w-3.5 h-3.5 mr-1" /> Open
                          </Button>
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </SectionShell>

          <SectionShell id="sec-timeline" icon={Clock} title="Timeline" count={history.length} accent="cyan">
            {history.length === 0 ? (
              <p className="text-slate-400 italic">No status events recorded yet.</p>
            ) : (
              <ol className="relative border-l border-slate-700 ml-2 space-y-3 pl-4">
                {history.map((e, idx) => (
                  <li key={idx} className="relative" data-testid={`intake-timeline-${idx}`}>
                    <span className={`absolute -left-[19px] top-1.5 w-2.5 h-2.5 rounded-full ring-4 ring-slate-900 ${
                      e.status === 'SUBMITTED' ? 'bg-blue-400' :
                      e.status === 'UNDER_REVIEW' ? 'bg-amber-400' :
                      e.status === 'APPROVED' ? 'bg-emerald-400' :
                      e.status === 'CONVERTED' ? 'bg-cyan-400' :
                      e.status === 'DECLINED' ? 'bg-red-400' :
                      'bg-slate-400'
                    }`} />
                    <p className="text-white text-sm font-medium">{e.status?.replace('_', ' ')}</p>
                    <p className="text-slate-500 text-xs">
                      {e.timestamp ? new Date(e.timestamp).toLocaleString() : 'N/A'}
                      {e.by_name && ` · by ${e.by_name}`}
                    </p>
                    {e.notes && <p className="text-slate-300 text-xs mt-1 italic">{e.notes}</p>}
                  </li>
                ))}
              </ol>
            )}
          </SectionShell>

          <SectionShell id="sec-notes" icon={MessageSquare} title="Internal Notes" count={notes.length} accent="purple">
            {notes.length === 0 ? (
              <p className="text-slate-400 italic mb-3">No internal notes yet.</p>
            ) : (
              <div className="space-y-2 mb-4">
                {notes.map((n, idx) => (
                  <div key={n.id || idx} className="p-3 bg-slate-700/40 rounded border border-slate-700"
                       data-testid={`intake-note-${idx}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-amber-300 text-xs font-mono">{showOrNA(n.author_name)}</p>
                      <p className="text-slate-500 text-[10px] font-mono">
                        {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                      </p>
                    </div>
                    <p className="text-white text-sm whitespace-pre-wrap">{n.content}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="border-t border-slate-700 pt-3">
              <Label className="text-slate-300 text-xs uppercase tracking-wider mb-1.5 block">Add Internal Note</Label>
              <Textarea
                value={newInternalNote}
                onChange={(e) => setNewInternalNote(e.target.value)}
                placeholder="Add any internal review notes or context for the team..."
                className="bg-slate-800 border-slate-700 text-white min-h-[80px]"
                data-testid="intake-note-textarea"
              />
              <div className="mt-2 flex justify-end">
                <Button size="sm" onClick={onAddNote} disabled={!newInternalNote.trim() || addingNote}
                        className="bg-purple-600 hover:bg-purple-700" data-testid="intake-add-note-btn">
                  {addingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><MessageSquare className="w-4 h-4 mr-1" /> Save Note</>)}
                </Button>
              </div>
            </div>
          </SectionShell>

          <SectionShell id="sec-signature" icon={FileSignature} title="Signature & Acknowledgments" accent="emerald">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-white text-base font-medium">{showOrNA(intake.signature?.typed_name)}</p>
                <p className="text-slate-500 text-xs">{showOrNA(intake.signature?.signature_date)}</p>
              </div>
              {intake.signature?.signature_image && (
                <img src={intake.signature?.signature_image} alt="Signature"
                     className="h-12 bg-white rounded px-2" />
              )}
            </div>
            <div className="flex gap-4 mt-3 text-xs flex-wrap">
              <span className={`flex items-center gap-1 ${intake.retainer_acknowledged ? 'text-emerald-400' : 'text-red-400'}`}>
                {intake.retainer_acknowledged ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                Retainer Acknowledged
              </span>
              <span className={`flex items-center gap-1 ${intake.legal_acknowledged ? 'text-emerald-400' : 'text-red-400'}`}>
                {intake.legal_acknowledged ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                Legal Notice Acknowledged
              </span>
            </div>
          </SectionShell>
        </div>
      </div>
    </div>
  );
};

const ClientIntakes = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showInfoRequestDialog, setShowInfoRequestDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [infoRequestMessage, setInfoRequestMessage] = useState('');
  const [newInternalNote, setNewInternalNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  const fetchIntakes = async () => {
    setLoading(true);
    try {
      const url = statusFilter === 'all'
        ? `${BACKEND_URL}/api/client-intakes`
        : `${BACKEND_URL}/api/client-intakes?status=${statusFilter}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setIntakes(data);
      }
    } catch (err) {
      console.error('Failed to fetch intakes:', err);
      toast({ title: 'Error', description: 'Failed to load intakes', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewIntake = async (intakeId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/client-intakes/${intakeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedIntake(data);
        setShowDetailDialog(true);
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load intake details', variant: 'destructive' });
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const url = statusFilter === 'all'
          ? `${BACKEND_URL}/api/client-intakes`
          : `${BACKEND_URL}/api/client-intakes?status=${statusFilter}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!alive) return;
        if (res.ok) {
          const data = await res.json();
          if (alive) setIntakes(data);
        }
      } catch (err) {
        if (alive) console.error('Failed to fetch intakes:', err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token, statusFilter]);

  const handleUpdateStatus = async () => {
    if (!selectedIntake || !newStatus) return;
    
    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append('status', newStatus);
      formData.append('notes', statusNotes);
      
      const res = await fetch(`${BACKEND_URL}/api/client-intakes/${selectedIntake.id}/status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        toast({ title: 'Success', description: 'Status updated successfully' });
        setShowStatusDialog(false);
        setNewStatus('');
        setStatusNotes('');
        fetchIntakes();
        // Refresh the selected intake
        handleViewIntake(selectedIntake.id);
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail, variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestInfo = async () => {
    if (!selectedIntake || !infoRequestMessage) return;
    
    setActionLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/client-intakes/${selectedIntake.id}/request-info`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: infoRequestMessage })
      });
      
      if (res.ok) {
        toast({ title: 'Success', description: 'Information request sent' });
        setShowInfoRequestDialog(false);
        setInfoRequestMessage('');
        fetchIntakes();
        handleViewIntake(selectedIntake.id);
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to send request', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!selectedIntake || !newInternalNote.trim()) return;
    setAddingNote(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/client-intakes/${selectedIntake.id}/notes`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: newInternalNote.trim() }),
      });
      if (res.ok) {
        toast({ title: 'Note added', description: 'Internal note saved to this intake.' });
        setNewInternalNote('');
        // Refresh detail
        const r2 = await fetch(`${BACKEND_URL}/api/client-intakes/${selectedIntake.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r2.ok) {
          const data = await r2.json();
          setSelectedIntake(data);
        }
        // Also refresh the list so the row notes count stays consistent
        fetchIntakes();
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail || 'Failed to add note', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to add note', variant: 'destructive' });
    } finally {
      setAddingNote(false);
    }
  };

  const handleConvertToCase = async () => {
    if (!selectedIntake) return;
    
    if (!window.confirm('Convert this intake to an active case? This will create a new case, client record, subjects, vehicles, and evidence entries.')) {
      return;
    }
    
    setActionLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/client-intakes/${selectedIntake.id}/convert-to-case`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast({ 
          title: 'Success', 
          description: `Converted to case ${data.case_number}` 
        });
        setShowDetailDialog(false);
        fetchIntakes();
        // Navigate to the new case
        navigate(`/cases/${data.case_id}`);
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail, variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to convert intake', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  // Filter intakes by search term
  const filteredIntakes = intakes.filter(intake => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      intake.client?.full_name?.toLowerCase().includes(term) ||
      intake.client?.email?.toLowerCase().includes(term) ||
      intake.id?.toLowerCase().includes(term) ||
      intake.case_details?.case_type?.toLowerCase().includes(term)
    );
  });

  // Stats
  const stats = {
    total: intakes.length,
    submitted: intakes.filter(i => i.status === 'SUBMITTED').length,
    underReview: intakes.filter(i => i.status === 'UNDER_REVIEW').length,
    moreInfo: intakes.filter(i => i.status === 'MORE_INFO_REQUESTED').length,
    approved: intakes.filter(i => i.status === 'APPROVED').length,
    converted: intakes.filter(i => i.status === 'CONVERTED').length,
  };

  return (
    <div className="space-y-6" data-testid="client-intakes-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-400" />
            Client Intake Requests
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Manage and process incoming case requests from the client portal
          </p>
        </div>
        <Button onClick={fetchIntakes} variant="outline" className="border-slate-600 text-slate-300">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-slate-600" onClick={() => setStatusFilter('all')}>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-white">{stats.total}</p>
            <p className="text-xs text-slate-400">Total</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-500/10 border-blue-500/30 cursor-pointer hover:border-blue-500/50" onClick={() => setStatusFilter('SUBMITTED')}>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-blue-300">{stats.submitted}</p>
            <p className="text-xs text-blue-400">New</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-500/10 border-amber-500/30 cursor-pointer hover:border-amber-500/50" onClick={() => setStatusFilter('UNDER_REVIEW')}>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-amber-300">{stats.underReview}</p>
            <p className="text-xs text-amber-400">Reviewing</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-500/10 border-purple-500/30 cursor-pointer hover:border-purple-500/50" onClick={() => setStatusFilter('MORE_INFO_REQUESTED')}>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-purple-300">{stats.moreInfo}</p>
            <p className="text-xs text-purple-400">Pending Info</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-500/10 border-emerald-500/30 cursor-pointer hover:border-emerald-500/50" onClick={() => setStatusFilter('APPROVED')}>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-emerald-300">{stats.approved}</p>
            <p className="text-xs text-emerald-400">Approved</p>
          </CardContent>
        </Card>
        <Card className="bg-cyan-500/10 border-cyan-500/30 cursor-pointer hover:border-cyan-500/50" onClick={() => setStatusFilter('CONVERTED')}>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-cyan-300">{stats.converted}</p>
            <p className="text-xs text-cyan-400">Converted</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Search by client name, email, or intake ID..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800/50 border-slate-700 text-white"
            data-testid="intake-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-48 bg-slate-800/50 border-slate-700 text-white">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="SUBMITTED">Submitted</SelectItem>
            <SelectItem value="UNDER_REVIEW">Under Review</SelectItem>
            <SelectItem value="MORE_INFO_REQUESTED">More Info Requested</SelectItem>
            <SelectItem value="APPROVED">Approved</SelectItem>
            <SelectItem value="DECLINED">Declined</SelectItem>
            <SelectItem value="CONVERTED">Converted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Intakes List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
        </div>
      ) : filteredIntakes.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-12 text-center">
            <FileText className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Intakes Found</h3>
            <p className="text-slate-400">
              {statusFilter !== 'all' 
                ? `No intakes with status "${statusFilter.replace('_', ' ')}"`
                : 'No client intake requests have been submitted yet.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredIntakes.map(intake => {
            const StatusIcon = STATUS_ICONS[intake.status] || Clock;
            return (
              <Card 
                key={intake.id} 
                className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors cursor-pointer"
                onClick={() => handleViewIntake(intake.id)}
                data-testid={`intake-card-${intake.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Status & ID */}
                    <div className="flex items-center gap-3 min-w-[200px]">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${STATUS_COLORS[intake.status]?.split(' ')[0] || 'bg-slate-700'}`}>
                        <StatusIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-mono text-sm text-white">{intake.id}</p>
                        <Badge className={`text-xs ${STATUS_COLORS[intake.status] || ''}`}>
                          {intake.status?.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Client Info */}
                    <div className="flex-1">
                      <p className="text-white font-medium">{intake.client?.full_name || 'Unknown Client'}</p>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-400 mt-1">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {intake.client?.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {intake.client?.primary_phone}
                        </span>
                      </div>
                    </div>
                    
                    {/* Case Type & Priority */}
                    <div className="text-right">
                      <p className="text-slate-300 text-sm">
                        {CASE_TYPE_LABELS[intake.case_details?.case_type] || intake.case_details?.case_type}
                      </p>
                      <div className="flex items-center justify-end gap-2 mt-1">
                        <Badge variant="outline" className={`text-xs ${
                          intake.case_details?.priority === 'EMERGENCY' ? 'border-red-500 text-red-400' :
                          intake.case_details?.priority === 'URGENT' ? 'border-amber-500 text-amber-400' :
                          'border-slate-600 text-slate-400'
                        }`}>
                          {intake.case_details?.priority || 'STANDARD'}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Counts */}
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      <span className="flex items-center gap-1" title="Subjects">
                        <Users className="w-4 h-4" />
                        {intake.subjects?.length || 0}
                      </span>
                      <span className="flex items-center gap-1" title="Vehicles">
                        <Car className="w-4 h-4" />
                        {intake.vehicles?.length || 0}
                      </span>
                      <span className="flex items-center gap-1" title="Files">
                        <Upload className="w-4 h-4" />
                        {intake.files?.length || 0}
                      </span>
                    </div>
                    
                    {/* Timestamp */}
                    <div className="text-right text-sm">
                      <p className="text-slate-500">
                        {new Date(intake.created_at).toLocaleDateString()}
                      </p>
                      <p className="text-slate-600 text-xs">
                        {new Date(intake.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                    
                    <ChevronRight className="w-5 h-5 text-slate-500" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Intake Detail Dialog — Full Intake Record */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-6xl max-h-[92vh] overflow-y-auto p-0" data-testid="intake-full-record-dialog">
          {selectedIntake && (
            <FullIntakeRecord
              intake={selectedIntake}
              actionLoading={actionLoading}
              addingNote={addingNote}
              newInternalNote={newInternalNote}
              setNewInternalNote={setNewInternalNote}
              onAddNote={handleAddNote}
              onUpdateStatus={() => setShowStatusDialog(true)}
              onRequestInfo={() => setShowInfoRequestDialog(true)}
              onConvertToCase={handleConvertToCase}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Update Intake Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UNDER_REVIEW">Under Review</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="DECLINED">Declined</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Notes (Optional)</Label>
              <Textarea
                value={statusNotes}
                onChange={e => setStatusNotes(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="Add any notes about this status change..."
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowStatusDialog(false)} className="border-slate-600">
                Cancel
              </Button>
              <Button onClick={handleUpdateStatus} disabled={!newStatus || actionLoading} className="bg-blue-600 hover:bg-blue-700">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Status'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Info Dialog */}
      <Dialog open={showInfoRequestDialog} onOpenChange={setShowInfoRequestDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Request More Information</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Message to Client</Label>
              <Textarea
                value={infoRequestMessage}
                onChange={e => setInfoRequestMessage(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white min-h-[120px]"
                placeholder="Describe what additional information you need from the client..."
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowInfoRequestDialog(false)} className="border-slate-600">
                Cancel
              </Button>
              <Button onClick={handleRequestInfo} disabled={!infoRequestMessage || actionLoading} className="bg-purple-600 hover:bg-purple-700">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Request'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientIntakes;
